import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Shield, EyeOff, Ban, Search, X, Loader2, Lock, Building2, Check
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConfidentialityPage = () => {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Anonymization state
  const [anonSettings, setAnonSettings] = useState(null);
  const [anonLocked, setAnonLocked] = useState(false);
  const [anonLoading, setAnonLoading] = useState(true);
  const [anonSaving, setAnonSaving] = useState(false);

  // Block companies state
  const [blockedCompanies, setBlockedCompanies] = useState([]);
  const [blockLocked, setBlockLocked] = useState(false);
  const [blockLoading, setBlockLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [blocking, setBlocking] = useState(null);

  useEffect(() => {
    fetchAnonymization();
    fetchBlockedCompanies();
  }, []);

  const fetchAnonymization = async () => {
    try {
      const res = await axios.get(`${API}/settings/anonymization`, { headers });
      if (res.data.feature_locked) {
        setAnonLocked(true);
      } else {
        setAnonSettings(res.data.settings);
      }
    } catch {
      toast.error('Failed to load anonymization settings');
    } finally {
      setAnonLoading(false);
    }
  };

  const fetchBlockedCompanies = async () => {
    try {
      const res = await axios.get(`${API}/settings/blocked-companies`, { headers });
      if (res.data.feature_locked) {
        setBlockLocked(true);
      } else {
        setBlockedCompanies(res.data.blocked_companies || []);
      }
    } catch {
      toast.error('Failed to load blocked companies');
    } finally {
      setBlockLoading(false);
    }
  };

  const saveAnonymization = async () => {
    setAnonSaving(true);
    try {
      await axios.put(`${API}/settings/anonymization`, {
        enabled: anonSettings.enabled,
        hide_company_name: anonSettings.hide_company_name,
        hide_company_logo: anonSettings.hide_company_logo,
        display_name: anonSettings.display_name
      }, { headers });
      toast.success('Anonymization settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setAnonSaving(false);
    }
  };

  const searchCompanies = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/companies/search?q=${encodeURIComponent(query)}`, { headers });
      // Filter out already blocked companies and own company
      const filtered = (res.data.companies || res.data || []).filter(
        c => !blockedCompanies.find(b => b.id === c.id) && c.id !== user?.company_id
      );
      setSearchResults(filtered.slice(0, 8));
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const blockCompany = async (companyId) => {
    setBlocking(companyId);
    try {
      await axios.post(`${API}/settings/blocked-companies/${companyId}`, {}, { headers });
      toast.success('Company blocked');
      setSearchQuery('');
      setSearchResults([]);
      fetchBlockedCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to block company');
    } finally {
      setBlocking(null);
    }
  };

  const unblockCompany = async (companyId) => {
    try {
      await axios.delete(`${API}/settings/blocked-companies/${companyId}`, { headers });
      toast.success('Company unblocked');
      fetchBlockedCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unblock company');
    }
  };

  const isPro = !anonLocked && !blockLocked;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="confidentiality-page">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Confidentiality & Control</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage your company's visibility and control who can see your listings.
        </p>
      </div>

      {/* Locked State */}
      {!isPro && (
        <Card className="border-amber-200 bg-amber-50" data-testid="feature-locked-banner">
          <CardContent className="py-6 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-amber-900">Professional Plan Required</p>
              <p className="text-sm text-amber-700 mt-0.5">
                Anonymization and company blocking are exclusive to the Professional plan. 
                Upgrade to control your visibility.
              </p>
            </div>
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0"
              onClick={() => window.location.href = '/pricing'}
              data-testid="upgrade-btn"
            >
              Upgrade
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Anonymization Section */}
      <Card className={anonLocked ? 'opacity-60 pointer-events-none' : ''} data-testid="anonymization-section">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <EyeOff className="w-5 h-5 text-gray-700" />
            <CardTitle className="text-lg">Anonymous Mode</CardTitle>
          </div>
          <p className="text-sm text-gray-500">
            Hide your company identity from other users. Your listings will appear under a generic name.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          {anonLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : anonSettings ? (
            <>
              {/* Master toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <Label className="font-medium">Enable Anonymous Mode</Label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    When enabled, your company name and logo will be hidden from other companies.
                  </p>
                </div>
                <Switch
                  checked={anonSettings.enabled}
                  onCheckedChange={(checked) =>
                    setAnonSettings({ ...anonSettings, enabled: checked })
                  }
                  data-testid="anon-toggle"
                />
              </div>

              {anonSettings.enabled && (
                <div className="space-y-4 pl-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Hide company name</Label>
                    <Switch
                      checked={anonSettings.hide_company_name}
                      onCheckedChange={(checked) =>
                        setAnonSettings({ ...anonSettings, hide_company_name: checked })
                      }
                      data-testid="hide-name-toggle"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Hide company logo</Label>
                    <Switch
                      checked={anonSettings.hide_company_logo}
                      onCheckedChange={(checked) =>
                        setAnonSettings({ ...anonSettings, hide_company_logo: checked })
                      }
                      data-testid="hide-logo-toggle"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Display name (shown instead)</Label>
                    <Input
                      className="mt-1.5"
                      value={anonSettings.display_name || ''}
                      onChange={(e) =>
                        setAnonSettings({ ...anonSettings, display_name: e.target.value })
                      }
                      placeholder="e.g. ServiceNow Partner"
                      data-testid="display-name-input"
                    />
                  </div>
                </div>
              )}

              <Button
                onClick={saveAnonymization}
                disabled={anonSaving}
                className="w-full"
                data-testid="save-anon-btn"
              >
                {anonSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Save Settings
              </Button>
            </>
          ) : null}
        </CardContent>
      </Card>

      {/* Block Companies Section */}
      <Card className={blockLocked ? 'opacity-60 pointer-events-none' : ''} data-testid="block-companies-section">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Ban className="w-5 h-5 text-gray-700" />
            <CardTitle className="text-lg">Block Companies</CardTitle>
          </div>
          <p className="text-sm text-gray-500">
            Prevent specific companies from seeing your listings. They won't know they've been blocked.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {blockLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Search to add */}
              <div className="relative">
                <div className="flex items-center bg-gray-50 border rounded-lg overflow-hidden">
                  <span className="px-3 text-gray-400">
                    <Search className="w-4 h-4" />
                  </span>
                  <Input
                    className="border-0 bg-transparent focus-visible:ring-0"
                    value={searchQuery}
                    onChange={(e) => searchCompanies(e.target.value)}
                    placeholder="Search companies to block..."
                    data-testid="block-search-input"
                  />
                  {searching && <Loader2 className="w-4 h-4 animate-spin text-gray-400 mr-3" />}
                </div>

                {/* Search Results Dropdown */}
                {searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto" data-testid="block-search-results">
                    {searchResults.map((company) => (
                      <button
                        key={company.id}
                        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 text-left"
                        onClick={() => blockCompany(company.id)}
                        disabled={blocking === company.id}
                        data-testid={`block-company-${company.id}`}
                      >
                        <div className="flex items-center gap-2">
                          {company.logo_url ? (
                            <img src={company.logo_url} alt="" className="w-6 h-6 rounded object-cover" />
                          ) : (
                            <Building2 className="w-6 h-6 text-gray-300" />
                          )}
                          <span className="text-sm font-medium text-gray-700">{company.name}</span>
                        </div>
                        {blocking === company.id ? (
                          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                        ) : (
                          <Ban className="w-4 h-4 text-red-400" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Blocked List */}
              {blockedCompanies.length > 0 ? (
                <div className="space-y-2" data-testid="blocked-companies-list">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                    Blocked ({blockedCompanies.length})
                  </p>
                  {blockedCompanies.map((company) => (
                    <div
                      key={company.id}
                      className="flex items-center justify-between p-2.5 bg-red-50 rounded-lg border border-red-100"
                      data-testid={`blocked-company-${company.id}`}
                    >
                      <div className="flex items-center gap-2">
                        {company.logo_url ? (
                          <img src={company.logo_url} alt="" className="w-6 h-6 rounded object-cover" />
                        ) : (
                          <Building2 className="w-6 h-6 text-gray-300" />
                        )}
                        <span className="text-sm font-medium text-gray-700">{company.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-100 h-7 px-2"
                        onClick={() => unblockCompany(company.id)}
                        data-testid={`unblock-${company.id}`}
                      >
                        <X className="w-3.5 h-3.5 mr-1" />
                        Unblock
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-400">
                  <Ban className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No companies blocked yet</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfidentialityPage;
